<template>
  <WelfareShell active="welfare-catalog" subtitle="รายการสวัสดิการที่ระบบใช้แนะนำให้ครัวเรือน — เพิ่ม แก้ไข กำหนดปีที่ใช้ เปิด/ปิดการใช้งาน และลากเพื่อจัดลำดับการแสดงผล">
    <template #actions>
      <button type="button" class="btn btn-outline-secondary" :disabled="loading" @click="load">
        <RefreshCwIcon class="w-4 h-4 mr-1" /> โหลดใหม่
      </button>
      <button type="button" class="btn btn-primary bg-primary-btn border-primary-btn" @click="openCreate">
        <PlusIcon class="w-4 h-4 mr-1" /> เพิ่มสวัสดิการ
      </button>
    </template>

    <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
      <div class="rounded-md border border-gray-200 bg-white p-3" style="border-top:3px solid #415A77">
        <div class="text-2xl font-semibold" style="color:#415A77">{{ items.length }}</div>
        <div class="text-sm text-gray-700">สวัสดิการทั้งหมด</div>
      </div>
      <div class="rounded-md border border-gray-200 bg-white p-3" style="border-top:3px solid #42B983">
        <div class="text-2xl font-semibold" style="color:#42B983">{{ stats.usable }}</div>
        <div class="text-sm text-gray-700">ใช้ได้วันนี้ <span class="text-xs text-theme-37">(เปิดใช้งานและอยู่ในช่วงเวลา)</span></div>
      </div>
      <div class="rounded-md border border-gray-200 bg-white p-3" style="border-top:3px solid #F78B00">
        <div class="text-2xl font-semibold" style="color:#F78B00">{{ stats.used }}</div>
        <div class="text-sm text-gray-700">มีกฎอ้างถึง</div>
      </div>
      <button type="button" class="rounded-md border border-gray-200 bg-white p-3 text-left" style="border-top:3px solid #CE3131" title="กดเพื่อดูรายการที่หมดอายุ" @click="fPeriod = 'expired'">
        <div class="text-2xl font-semibold" style="color:#CE3131">{{ stats.expired }}</div>
        <div class="text-sm text-gray-700">หมดอายุแล้ว <span class="text-xs text-theme-37">(ยังไม่ถูกใช้ {{ stats.unused }})</span></div>
      </button>
    </div>

    <div class="box p-4">
      <div class="flex flex-col lg:flex-row lg:items-center gap-2 mb-3">
        <div class="relative flex-1">
          <SearchIcon class="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-theme-37" />
          <input v-model.trim="q" type="text" class="form-control pl-9" placeholder="ค้นหาชื่อสวัสดิการ หน่วยงาน หรือรหัส">
        </div>
        <select v-model="fPeriod" class="form-select lg:w-44" aria-label="ช่วงเวลาที่ใช้"><option v-for="o in periodOptions" :key="o.value" :value="o.value">{{ o.label }}</option></select>
        <select v-model="fDim" class="form-select lg:w-44"><option value="">ทุกมิติ</option><option v-for="d in DIMENSIONS" :key="d" :value="d">{{ d }}</option></select>
        <select v-model="fLevel" class="form-select lg:w-36"><option value="">ทุกระดับ</option><option v-for="(l, k) in CATALOG_LEVEL_LABEL" :key="k" :value="k">{{ l }}</option></select>
        <select v-model="fStatus" class="form-select lg:w-36"><option value="">ทุกสถานะ</option><option value="active">เปิดใช้งาน</option><option value="inactive">ปิดใช้งาน</option></select>
        <select v-model="fUsage" class="form-select lg:w-40"><option value="">การใช้งานทั้งหมด</option><option value="used">มีกฎอ้างถึง</option><option value="unused">ยังไม่ถูกใช้</option></select>
      </div>
      <div class="flex flex-wrap items-center justify-between gap-2 text-xs text-theme-37 mb-2">
        <span>แสดง {{ filtered.length }} จาก {{ items.length }} รายการ</span>
        <span v-if="canDrag" class="inline-flex items-center gap-1"><MoveIcon class="w-3.5 h-3.5" /> ลากที่ไอคอนหน้าแถวเพื่อจัดลำดับ ระบบบันทึกให้ทันที</span>
        <button v-else type="button" class="underline" @click="clearFilters">ล้างตัวกรองเพื่อจัดลำดับด้วยการลาก</button>
      </div>

      <div v-if="loading" class="py-10 text-center text-theme-37"><LoadingIcon icon="oval" class="w-8 h-8 mx-auto" /> กำลังโหลด…</div>
      <div v-else class="overflow-x-auto">
        <table class="table table-admin w-full">
          <thead>
            <tr>
              <th class="w-8"></th>
              <th class="w-16">รหัส</th>
              <th>สวัสดิการ</th>
              <th>ใช้กับปี</th>
              <th>มิติ</th>
              <th class="w-24">ระดับ</th>
              <th>สิทธิประโยชน์</th>
              <th class="w-28">ใช้ในกฎ</th>
              <th class="w-20 text-center">สถานะ</th>
              <th class="w-24 text-center">จัดการ</th>
            </tr>
          </thead>
          <tbody ref="tbodyRef">
            <tr v-for="it in filtered" :key="it.welfare_id" :data-id="it.welfare_id" :class="{ 'opacity-60': it.active === false }">
              <td class="align-middle"><span v-if="canDrag" class="drag-handle inline-flex" title="ลากเพื่อจัดลำดับ"><MoreVerticalIcon class="w-4 h-4 -mr-2" /><MoreVerticalIcon class="w-4 h-4" /></span></td>
              <td class="text-sm text-theme-37 whitespace-nowrap">{{ it.welfare_id }}</td>
              <td class="min-w-[16rem]">
                <div class="font-medium text-gray-800 break-words">{{ it.name }}</div>
                <div v-if="it.agency || it.ministry" class="text-xs text-theme-37 break-words mt-0.5">{{ it.agency }}<span v-if="it.agency && it.ministry"> · </span>{{ it.ministry }}</div>
                <a v-if="it.url" :href="it.url" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-1 text-xs mt-1" style="color:#415A77">เว็บไซต์หน่วยงาน <ExternalLinkIcon class="w-3 h-3" /></a>
              </td>
              <td class="whitespace-nowrap"><PeriodChip :period="it.period" /></td>
              <td><DimensionChips :dimensions="it.dimensions" placeholder="ยังไม่ระบุมิติ" /></td>
              <td><span class="chip chip-muted">{{ CATALOG_LEVEL_LABEL[it.level] || 'ไม่ระบุ' }}</span></td>
              <td class="text-sm text-gray-700 max-w-xs"><div class="clamp-3 break-words" :title="benefitText(it)">{{ benefitText(it) || '-' }}</div></td>
              <td class="text-sm whitespace-nowrap">
                <template v-if="it.usage && it.usage.rule_ids.length">
                  <span class="chip chip-outline" :title="it.usage.rule_ids.join(', ')">{{ it.usage.rule_ids.length }} กฎ</span>
                  <span v-if="it.usage.problem_rule_ids.length" class="chip ml-1" :style="SEVERITY_STYLE.urgent">{{ it.usage.problem_rule_ids.length }} ปัญหา</span>
                </template>
                <span v-else class="text-xs text-theme-37">ยังไม่ถูกใช้</span>
              </td>
              <td class="text-center align-middle">
                <input type="checkbox" class="form-check-switch" :checked="it.active !== false" :title="it.active !== false ? 'เปิดใช้งาน' : 'ปิดใช้งาน'" @change="toggleActive(it, $event)">
              </td>
              <td class="text-center align-middle whitespace-nowrap">
                <button type="button" class="icon-btn icon-btn-edit mr-1" title="แก้ไข" @click="openEdit(it)"><Edit2Icon class="w-4 h-4" /></button>
                <button type="button" class="icon-btn icon-btn-del" title="ลบ" @click="remove(it)"><Trash2Icon class="w-4 h-4" /></button>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="!filtered.length" class="empty-state">ไม่พบรายการที่ตรงกับตัวกรอง</div>
      </div>
    </div>

    <CatalogForm :show="showForm" :item="editing" @close="showForm = false" @saved="onSaved" />
  </WelfareShell>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from "vue";
import WelfareShell from "../components/WelfareShell.vue";
import DimensionChips from "../components/DimensionChips.vue";
import PeriodChip from "../components/PeriodChip.vue";
import CatalogForm from "./CatalogForm.vue";
import { welfareApi, apiErrorMessage } from "../api";
import { DIMENSIONS, CATALOG_LEVEL_LABEL, BENEFIT_TYPE_LABEL, SEVERITY_STYLE } from "../constants";
import { useSortable, moveItem, revertDom } from "../useSortable";
import { isUsableToday, periodStatus, periodFilterOptions, matchPeriodFilter } from "../period";
import { toastSuccess, toastError, confirmDialog, alertError } from "../components/useToast";

const items = ref([]);
const loading = ref(false);
const q = ref("");
const fDim = ref("");
const fLevel = ref("");
const fStatus = ref("");
const fUsage = ref("");
const fPeriod = ref("");
const showForm = ref(false);
const editing = ref(null);
const tbodyRef = ref(null);

const stats = computed(() => ({
  usable: items.value.filter((i) => i.active !== false && isUsableToday(i.period)).length,
  expired: items.value.filter((i) => periodStatus(i.period) === "expired").length,
  used: items.value.filter((i) => i.usage && i.usage.rule_ids.length).length,
  unused: items.value.filter((i) => !(i.usage && i.usage.rule_ids.length)).length,
}));

const periodOptions = computed(() => { const o = periodFilterOptions(items.value); return [o[1], o[0], ...o.slice(2)]; });   // "all" first here
const filtered = computed(() => {
  const s = q.value.toLowerCase();
  return items.value.filter((i) => {
    if (!matchPeriodFilter(i.period, fPeriod.value)) return false;
    if (s && !(`${i.welfare_id} ${i.name || ""} ${i.agency || ""} ${i.ministry || ""}`.toLowerCase().includes(s))) return false;
    if (fDim.value && !(i.dimensions || []).includes(fDim.value)) return false;
    if (fLevel.value && (i.level || "unknown") !== fLevel.value) return false;
    if (fStatus.value === "active" && i.active === false) return false;
    if (fStatus.value === "inactive" && i.active !== false) return false;
    const used = !!(i.usage && i.usage.rule_ids.length);
    if (fUsage.value === "used" && !used) return false;
    if (fUsage.value === "unused" && used) return false;
    return true;
  });
});
const canDrag = computed(() => !q.value && !fDim.value && !fLevel.value && !fStatus.value && !fUsage.value && !fPeriod.value);
const clearFilters = () => { q.value = ""; fDim.value = ""; fLevel.value = ""; fStatus.value = ""; fUsage.value = ""; fPeriod.value = ""; };

const benefitText = (it) => {
  const b = it.benefit || {};
  const parts = [];
  if (b.type) parts.push(BENEFIT_TYPE_LABEL[b.type] || b.type);
  if (b.amount_baht) parts.push(`${Number(b.amount_baht).toLocaleString("th-TH")} บาท${b.frequency ? "/" + b.frequency : ""}`);
  else if (b.frequency) parts.push(b.frequency);
  if (b.description) parts.push(b.description);
  return parts.join(" · ");
};

async function load() {
  loading.value = true;
  try {
    const res = await welfareApi.catalog.list();
    items.value = res.items || [];
  } catch (e) {
    toastError(apiErrorMessage(e, "โหลดรายการสวัสดิการไม่สำเร็จ"));
  } finally {
    loading.value = false;
    await nextTick();
    ensureSortable();
  }
}

// ---- drag & drop ordering ----
const { attach, destroyAll } = useSortable({
  handle: ".drag-handle",
  onEnd: async (evt) => {
    const { oldIndex, newIndex } = evt;
    revertDom(evt);
    if (oldIndex === newIndex || !canDrag.value) return;
    moveItem(items.value, oldIndex, newIndex);
    try {
      await welfareApi.catalog.reorder(items.value.map((i) => i.welfare_id));
      items.value.forEach((i, idx) => { i.sort_order = idx; });
      toastSuccess("บันทึกลำดับแล้ว");
    } catch (e) {
      toastError(apiErrorMessage(e, "บันทึกลำดับไม่สำเร็จ"));
      await load();
    }
  },
});
let sortable = null;
function ensureSortable() {
  if (!tbodyRef.value) return;
  if (sortable && sortable.el === tbodyRef.value) { sortable.option("disabled", !canDrag.value); return; }
  destroyAll();
  sortable = attach(tbodyRef.value, { disabled: !canDrag.value });
}
watch(canDrag, (v) => { if (sortable) sortable.option("disabled", !v); });

// ---- actions ----
const openCreate = () => { editing.value = null; showForm.value = true; };
const openEdit = (it) => { editing.value = it; showForm.value = true; };
const onSaved = async () => { showForm.value = false; await load(); };

async function toggleActive(it, evt) {
  const active = evt.target.checked;
  try {
    await welfareApi.catalog.update(it.welfare_id, { active });
    it.active = active;
    toastSuccess(active ? "เปิดใช้งานแล้ว" : "ปิดใช้งานแล้ว ระบบจะไม่แนะนำสวัสดิการนี้");
  } catch (e) {
    evt.target.checked = !active;
    toastError(apiErrorMessage(e));
  }
}

async function remove(it) {
  const ok = await confirmDialog({ title: "ยืนยันลบสวัสดิการ", text: it.name, confirmText: "ลบ" });
  if (!ok) return;
  try {
    await welfareApi.catalog.remove(it.welfare_id);
    toastSuccess("ลบแล้ว");
    await load();
  } catch (e) {
    const d = e.response && e.response.data;
    if (e.response && e.response.status === 409 && d && d.data && d.data.rule_ids) {
      const force = await confirmDialog({
        title: "สวัสดิการนี้ถูกใช้ในกฎ",
        html: `<div class="text-left text-sm">ถูกอ้างถึงโดยกฎ ${d.data.rule_ids.length} ข้อ:<br><span class="text-theme-37">${d.data.rule_ids.join(", ")}</span><br><br>ต้องการถอดออกจากกฎเหล่านั้นและลบสวัสดิการนี้หรือไม่?</div>`,
        confirmText: "ถอดออกและลบ",
      });
      if (!force) return;
      try {
        await welfareApi.catalog.remove(it.welfare_id, true);
        toastSuccess("ลบและถอดออกจากกฎแล้ว");
        await load();
      } catch (e2) {
        alertError(apiErrorMessage(e2));
      }
      return;
    }
    alertError(apiErrorMessage(e));
  }
}

onMounted(load);
</script>
